import { formatDate } from 'shared/time/businessTime';
const readString = (value) => (typeof value === 'string' && value.trim() ? value.trim() : '');

/** Auto-generated names, including the `Initiative <id> changes` an Initiative draft gets. */
const GENERIC_SANDBOX_NAME_PATTERNS = [
  /^sandbox\s+[0-9a-f-]{4,}/i,
  /^initiative\s+[0-9a-f-]{8,}/i,
];

export const isGenericSandboxName = (name) => {
  const normalized = readString(name);
  if (!normalized) return true;
  return GENERIC_SANDBOX_NAME_PATTERNS.some((pattern) => pattern.test(normalized));
};

const truncate = (value, maxLength) => {
  const text = readString(value);
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}…`;
};

/**
 * Primary label for a draft — never a raw sandbox id. Callers that have their own
 * context can supply a better fallback than "Untitled draft".
 */
export const getDraftTitle = (sandbox, { fallback = 'Untitled draft' } = {}) => {
  const name = readString(sandbox?.sandbox_name);
  const description = readString(sandbox?.description);

  if (name && !isGenericSandboxName(name)) return name;

  if (description) {
    const firstSentence = description.split(/\n|[.!?](?:\s|$)/)[0]?.trim();
    if (firstSentence) return truncate(firstSentence, 80);
  }

  return fallback;
};

/**
 * Secondary line when the title is a proper name and a description exists.
 */
export const getDraftSubtitle = (sandbox) => {
  const name = readString(sandbox?.sandbox_name);
  const description = readString(sandbox?.description);

  if (!description) return '';
  if (!name || isGenericSandboxName(name)) return '';
  if (description === getDraftTitle(sandbox)) return '';

  return truncate(description, 140);
};

const ARTIFACT_FAMILY_LABELS = {
  view: 'App',
  action: 'Action',
  agent: 'Agent',
  automation: 'Automation',
  initiative_template: 'Work template',
  ontology_schema_delta: 'Ontology',
  operational_process_version: 'Process map',
  integration_definition: 'Connection',
  data_retrieval_contract: 'Data query',
  code_execution_program: 'Program',
};

const summaryHasDataChanges = (summary) =>
  [
    'object_upsert_count',
    'object_delete_count',
    'relationship_upsert_count',
    'relationship_delete_count',
  ].some((key) => Number(summary[key] || 0) > 0);

/**
 * Human-readable change tags — what changed, not how many rows.
 */
export const withDraftChangeSummary = (sandbox, changes = null) => {
  if (!sandbox) return sandbox;
  const existing =
    sandbox.artifact_summary?.by_family || sandbox.summary?.artifact_summary?.by_family || {};
  if (Object.values(existing).some((count) => Number(count) > 0)) return sandbox;
  const artifacts = Array.isArray(changes?.artifacts) ? changes.artifacts : [];
  const byFamily = {};
  artifacts.forEach((artifact) => {
    const family = readString(artifact?.family);
    if (!family) return;
    byFamily[family] = (byFamily[family] || 0) + 1;
  });
  if (!Object.keys(byFamily).length) return sandbox;
  return {
    ...sandbox,
    artifact_summary: {
      by_family: byFamily,
      total_count: artifacts.length,
    },
  };
};

export const getDraftChangeTags = (sandbox) => {
  const summary = sandbox?.summary && typeof sandbox.summary === 'object' ? sandbox.summary : {};
  const tags = [];
  const seen = new Set();

  const addTag = (key, label) => {
    if (!label || seen.has(key)) return;
    seen.add(key);
    tags.push({ key, label });
  };

  const affectedTypes = Array.isArray(summary.affected_object_types)
    ? summary.affected_object_types.filter(Boolean).map(String)
    : [];
  if (affectedTypes.length > 0) {
    const preview = affectedTypes.slice(0, 2).join(', ');
    const suffix = affectedTypes.length > 2 ? ` +${affectedTypes.length - 2} more` : '';
    addTag('data-types', `Touches ${preview}${suffix}`);
  } else if (summaryHasDataChanges(summary)) {
    addTag('data', 'Records updated');
  }

  const artifactSummary =
    (sandbox?.artifact_summary && typeof sandbox.artifact_summary === 'object'
      ? sandbox.artifact_summary
      : null) ||
    (summary.artifact_summary && typeof summary.artifact_summary === 'object'
      ? summary.artifact_summary
      : {});
  const artifactByFamily =
    artifactSummary.by_family && typeof artifactSummary.by_family === 'object'
      ? artifactSummary.by_family
      : {};

  Object.entries(artifactByFamily).forEach(([family, count]) => {
    if (Number(count || 0) <= 0) return;
    const label = ARTIFACT_FAMILY_LABELS[family] || family.replace(/_/g, ' ');
    addTag(`artifact-${family}`, `Draft ${label.toLowerCase()}`);
  });

  if (Number(summary.view_upsert_count || 0) > 0) {
    addTag('view-upsert', 'App updated');
  }
  if (Number(summary.view_delete_count || 0) > 0) {
    addTag('view-delete', 'App removed');
  }
  if (Number(summary.action_upsert_count || 0) > 0) {
    addTag('action', 'Action updated');
  }
  if (tags.length === 0) {
    addTag('empty', 'No proposed changes');
  }

  return tags.slice(0, 4);
};

export const getDraftFreshnessNotice = (sandbox) => {
  const status = readString(sandbox?.freshness_status)?.toLowerCase();
  if (status === 'stale') return 'Workspace data has changed — review before applying';
  if (status === 'conflicted') return 'Conflicts with current workspace data — review before applying';
  if (status === 'expired') return 'Preview may no longer reflect your current workspace';
  return '';
};

export const formatRelativeDraftTime = (isoValue) => {
  if (!isoValue) return '';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return 'Just now';

  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return formatDate(isoValue, { month: 'short', day: 'numeric' });
};

export const sortDraftsForDisplay = (sandboxes = []) =>
  [...sandboxes].sort((left, right) => {
    const leftTime = new Date(left?.updated_at || left?.created_at || 0).getTime();
    const rightTime = new Date(right?.updated_at || right?.created_at || 0).getTime();
    return rightTime - leftTime;
  });

/**
 * A draft is "active" (selectable as a preview) until it is published or thrown away.
 */
export const isActiveDraftStatus = (status) => {
  const normalized = readString(status).toLowerCase();
  return normalized !== 'published' && normalized !== 'discarded' && normalized !== 'archived';
};

/** Active drafts, newest first — the single source of truth for any preview chooser. */
export const selectActiveDrafts = (sandboxes = []) =>
  sortDraftsForDisplay((sandboxes || []).filter((sandbox) => isActiveDraftStatus(sandbox?.sandbox_status)));
